import React, { useEffect, useState } from "react";
import { Cpu, Server, CheckCircle2, XCircle, Loader2, Sparkles, Cloud, HardDrive as HD, Activity } from "lucide-react";
import { toast } from "sonner";
import { api, fmtBytes } from "@/lib/api";
import { GpuProfile } from "@/components/settings/GpuProfile";
import { DesktopSettings } from "@/components/settings/DesktopSettings";
import { ProjectTransfer } from "@/components/settings/ProjectTransfer";
import { StorageMeter } from "@/components/settings/StorageMeter";
import { useDesktop } from "@/hooks/useDesktop";
import { useApp } from "@/context/AppContext";

function ProviderCard({ p, onToggle }) {
  const [health, setHealth] = useState(null);
  const [checking, setChecking] = useState(false);

  const check = async () => {
    setChecking(true);
    try { setHealth(await api.aiHealth(p.name)); }
    catch (e) { setHealth({ ok: false, detail: "check failed" }); }
    finally { setChecking(false); }
  };
  useEffect(() => { check(); /* eslint-disable-next-line */ }, []);

  return (
    <div className="studio-card p-5" data-testid={`provider-card-${p.name}`}>
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: p.kind === "cloud" ? "rgba(0,240,255,0.12)" : "rgba(16,185,129,0.12)" }}>
            {p.kind === "cloud" ? <Cloud size={18} className="text-[#00F0FF]" /> : <HD size={18} className="text-[#10B981]" />}
          </div>
          <div>
            <div className="font-head font-semibold">{p.display_name}</div>
            <div className="metric-label" style={{ fontSize: 9 }}>{p.kind} · {p.name}</div>
          </div>
        </div>
        <button
          data-testid={`provider-toggle-${p.name}`}
          onClick={() => onToggle(p)}
          className={`relative w-11 h-6 rounded-full transition-colors ${p.enabled ? "bg-[#E6B04E]" : "bg-[#252A3B]"}`}
        >
          <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${p.enabled ? "translate-x-5" : ""}`} />
        </button>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {p.capabilities.map((c) => <span key={c} className="tech-badge">{c.replace(/_/g, " ")}</span>)}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-[#252A3B]">
        <div className="flex items-center gap-2 text-[12px] font-mono">
          {checking ? <Loader2 size={14} className="animate-spin text-[#94A3B8]" />
            : health?.ok ? <CheckCircle2 size={14} className="text-[#10B981]" />
            : <XCircle size={14} className="text-[#EF4444]" />}
          <span className={health?.ok ? "text-[#10B981]" : "text-[#94A3B8]"}>{checking ? "Pinging…" : (health?.detail || "unknown")}</span>
        </div>
        <button data-testid={`provider-health-${p.name}`} className="btn btn-ghost !py-1.5 !px-3 text-[12px]" onClick={check} disabled={checking}>
          <Activity size={13} /> Health Check
        </button>
      </div>
    </div>
  );
}

export default function Settings() {
  const desktop = useDesktop();
  const { system } = useApp();
  const [data, setData] = useState(null);

  const load = async () => { try { setData(await api.aiProviders()); } catch (e) {} };
  useEffect(() => { load(); }, []);

  const toggle = async (p) => {
    try { await api.aiUpdate(p.name, { enabled: !p.enabled }); toast.success(`${p.display_name} ${!p.enabled ? "enabled" : "disabled"}`); load(); }
    catch (e) { toast.error("Failed to update"); }
  };

  const gpu = system?.gpu;
  const cpu = system?.cpu;

  return (
    <div className="p-8 fade-up space-y-6 max-w-5xl">
      <div>
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Settings</h1>
        <p className="text-[#94A3B8] text-sm mt-1">AI providers, hardware and task routing</p>
      </div>

      {/* system */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="studio-card p-4" data-testid="sys-gpu">
          <div className="flex items-center gap-2 mb-2"><Cpu size={15} className="text-[#00F0FF]" /><span className="metric-label">GPU</span></div>
          <div className="font-head font-bold truncate">{gpu?.available ? gpu.name : "CPU only"}</div>
          <div className="metric-label" style={{ fontSize: 9 }}>{gpu?.available ? `${gpu.vram_mb}MB · ${gpu.utilization}%` : "no hardware accel"}</div>
        </div>
        <div className="studio-card p-4" data-testid="sys-cpu">
          <div className="flex items-center gap-2 mb-2"><Server size={15} className="text-[#E6B04E]" /><span className="metric-label">CPU</span></div>
          <div className="font-head font-bold">{cpu?.cores ?? "—"} cores</div>
          <div className="metric-label" style={{ fontSize: 9 }}>{cpu?.percent ?? 0}% load</div>
        </div>
        <div className="studio-card p-4" data-testid="sys-ram">
          <div className="flex items-center gap-2 mb-2"><HD size={15} className="text-[#8B5CF6]" /><span className="metric-label">RAM</span></div>
          <div className="font-head font-bold">{fmtBytes(cpu?.ram_used || 0)}</div>
          <div className="metric-label" style={{ fontSize: 9 }}>of {fmtBytes(cpu?.ram_total || 0)}</div>
        </div>
        <div className="studio-card p-4" data-testid="sys-ffmpeg">
          <div className="flex items-center gap-2 mb-2"><Sparkles size={15} className="text-[#10B981]" /><span className="metric-label">FFmpeg</span></div>
          <div className="font-head font-bold" style={{ color: system?.ffmpeg ? "#10B981" : "#EF4444" }}>{system?.ffmpeg ? "Available" : "Missing"}</div>
          <div className="metric-label" style={{ fontSize: 9 }}>media engine</div>
        </div>
      </div>

      {desktop?.desktop && <DesktopSettings info={desktop} />}
      <StorageMeter />
      <ProjectTransfer />
      <GpuProfile />

      {/* providers */}
      <div>
        <h2 className="font-head text-xl font-bold mb-3">AI Providers</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(data?.providers || []).map((p) => <ProviderCard key={p.name} p={p} onToggle={toggle} />)}
        </div>
      </div>

      {/* routing */}
      {data?.routes && (
        <div>
          <h2 className="font-head text-xl font-bold mb-3">AI Task Routing</h2>
          <div className="studio-card p-5">
            <p className="text-[#94A3B8] text-sm mb-4">The orchestrator selects the best healthy provider per task, with automatic fallback.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2.5">
              {Object.entries(data.routes).map(([task, chain]) => (
                <div key={task} className="flex items-center justify-between border-b border-[#252A3B] pb-2">
                  <span className="metric-label">{task.replace(/_/g, " ")}</span>
                  <span className="font-mono text-[12px] text-[#CBD5E1]">{chain.join(" → ")}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
